import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { Spinner } from '@/components/ui'
import { Label } from '@/components/ui/label'

import { ERoleType } from '../constants'
import { useGetUserById } from '../hooks'
import { UserManagementUser } from '../types'

import { ProjectAccess } from './AssignedAccess/ProjectAccess'

type TEditUserAccessProps = {
  user: UserManagementUser
  selectedProjectIds: string[]
  setSelectedProjectIds: (projects: string[], isInitial?: boolean) => void
}

const EditUserAccess: React.FC<TEditUserAccessProps> = (props) => {
  const { user, selectedProjectIds, setSelectedProjectIds } = props

  const { t } = useTranslation()

  const { data: { data: [data] = [] } = {}, isLoading } = useGetUserById(
    user.id
  )

  React.useEffect(() => {
    if (!data) return

    // Get roles that are project lead or team member
    const expectedRole = data.roles?.find((r) =>
      [ERoleType.PROJECT_LEAD, ERoleType.TEAM_MEMBER].includes(
        r.role_name as ERoleType
      )
    )
    if (!expectedRole) return

    // Get scopes for the roles
    if (!expectedRole.scopes?.length) return

    // Get project ids from scopes
    const projectIds: string[] = []
    expectedRole.scopes.forEach((scope) => {
      if (scope?.project_id) projectIds.push(scope.project_id)
    })

    // Set selected projects (isInitial is true to indicate that this is the initial load)
    setSelectedProjectIds(projectIds, true)
  }, [data, setSelectedProjectIds])

  return (
    <div className="space-y-2">
      <Label htmlFor="access">{t('userManagement.modal.access')}</Label>
      {isLoading ? (
        <div className="flex items-center justify-center py-2 h-40">
          <Spinner className="size-4 text-brand-600" />
        </div>
      ) : (
        <ProjectAccess
          selectedProjects={selectedProjectIds}
          setSelectedProjects={setSelectedProjectIds}
          containerClassName="max-h-48"
        />
      )}
    </div>
  )
}

const EditUserAccessMemoized = React.memo(EditUserAccess)
EditUserAccessMemoized.displayName = 'EditUserAccess'

export { EditUserAccessMemoized as EditUserAccess }
